import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import fallbackSponsorData from '../../data/sponsors.json';

export default function Brand() {
    const [section, setSection] = useState(fallbackSponsorData.section);
    const [tiers, setTiers] = useState(fallbackSponsorData.tiers);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSponsors();
    }, []);

    const fetchSponsors = async () => {
        try {
            const [tRes, sRes, pRes] = await Promise.all([
                supabase.from('sponsor_tiers').select('*').order('order_idx', { ascending: true }),
                supabase.from('sponsors').select('*').order('created_at', { ascending: true }),
                supabase.from('sponsors_page_content').select('*').eq('page_name', 'sponsors-page').single()
            ]);

            if (!tRes.error && tRes.data) {
                // Combine tiers with their sponsors
                const combinedTiers = tRes.data.map(tier => ({
                    ...tier,
                    sponsors: sRes.data ? sRes.data.filter(s => s.tier_id === tier.id) : []
                }));
                // Only keep tiers that have at least one sponsor, to match JSON logic
                setTiers(combinedTiers.filter(t => t.sponsors.length > 0));
            }

            if (!pRes.error && pRes.data && pRes.data.data) {
                setSection(pRes.data.data);
            }
        } catch (err) {
            console.error("Error fetching sponsors, using fallback", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return null; // Or a subtle loader, but null is fine to prevent flash of wrong data if loading is fast

    // If there are no sponsors across any tiers, hide the entire section
    if (tiers.length === 0) return null;

    return (
        <section className="sp-section bsides-sponsors">
            <style>{`
                .sp-section {
                    background: var(--eventflow-black);
                    padding: 0 0 80px;
                    position: relative;
                    overflow: hidden;
                }

                /* ── Header ── */
                .sp-header {
                    text-align: center;
                    margin-bottom: 64px;
                }
                .sp-header__eyebrow {
                    font-family: var(--eventflow-font);
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    color: var(--eventflow-base);
                    margin-bottom: 16px;
                }
                .sp-header__title {
                    font-family: var(--eventflow-font);
                    font-size: 42px;
                    font-weight: 900;
                    color: #fff;
                    line-height: 1.2;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                    letter-spacing: -0.5px;
                }
                .sp-header__title span {
                    color: var(--eventflow-base);
                }
                .sp-header__sub {
                    font-family: var(--eventflow-font);
                    font-size: 14px;
                    color: rgba(255,255,255,0.4);
                    max-width: 480px;
                    margin: 0 auto;
                    line-height: 1.6;
                }

                /* ── Tier Block ── */
                .sp-tier {
                    margin-bottom: 16px;
                    padding: 24px 0;
                    border-top: 1px solid rgba(255,255,255,0.04);
                }
                .sp-tier:first-of-type {
                    border-top: none;
                }
                .sp-tier__heading {
                    text-align: center;
                    margin-bottom: 28px;
                }
                .sp-tier__sub {
                    font-family: var(--eventflow-font);
                    font-size: 10px;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: rgba(255,255,255,0.25);
                    margin-bottom: 6px;
                }
                .sp-tier__name {
                    font-family: var(--eventflow-font);
                    font-size: 20px;
                    font-weight: 800;
                    color: #fff;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .sp-tier__name span {
                    color: var(--eventflow-base);
                }

                /* ── Logo Grid ── */
                .sp-logos {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 20px;
                }

                /* ── Logo Card ── */
                .sp-logo-card {
                    background: #fff;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    transition: all 0.3s ease;
                    text-decoration: none;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
                    border: 1px solid rgba(0,0,0,0.04);
                    overflow: hidden;
                }
                .sp-logo-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.15);
                }
                .sp-logo-card img {
                    display: block;
                    width: auto;
                    height: auto;
                    max-width: 85%;
                    max-height: 85%;
                    object-fit: contain;
                    border-radius: 12px;
                }
                .sp-logo-placeholder {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    font-family: var(--eventflow-font);
                    font-size: 16px;
                    font-weight: 700;
                    color: rgba(0,0,0,0.35);
                    text-transform: uppercase;
                    letter-spacing: 2px;
                    padding: 20px;
                    text-align: center;
                }

                /* Size variants based on tier importance */
                .sp-logo-card--title {
                    width: 400px;
                    max-width: 100%;
                    height: 200px;
                }
                .sp-logo-card--large {
                    min-width: 280px;
                    min-height: 140px;
                }
                .sp-logo-card--medium {
                    min-width: 220px;
                    min-height: 120px;
                }
                .sp-logo-card--small {
                    min-width: 180px;
                    min-height: 100px;
                }

                /* ── CTA Section ── */
                .sp-cta {
                    margin-top: 32px;
                    background: rgba(var(--eventflow-base-rgb), 0.04);
                    border: 1px dashed rgba(var(--eventflow-base-rgb), 0.18);
                    border-radius: 16px;
                    padding: 40px 48px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 32px;
                    flex-wrap: wrap;
                }
                .sp-cta__text h4 {
                    font-family: var(--eventflow-font);
                    font-size: 22px;
                    font-weight: 900;
                    color: #fff;
                    margin-bottom: 6px;
                }
                .sp-cta__text p {
                    font-family: var(--eventflow-font);
                    font-size: 14px;
                    color: rgba(255,255,255,0.4);
                    margin: 0;
                }
                .sp-cta__tiers {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .sp-cta__pill {
                    font-family: var(--eventflow-font);
                    font-size: 11px;
                    font-weight: 600;
                    padding: 5px 14px;
                    border-radius: 20px;
                    border: 1px solid rgba(255,255,255,0.1);
                    color: rgba(255,255,255,0.45);
                    background: rgba(255,255,255,0.03);
                    letter-spacing: 0.5px;
                }

                /* ── Responsive ── */
                @media (max-width: 768px) {
                    .sp-section { padding: 60px 0 60px; }
                    .sp-header__title { font-size: 30px; }
                    .sp-tier { padding: 32px 0; margin-bottom: 24px; }
                    .sp-logo-card--title { min-width: 240px; padding: 24px 32px; }
                    .sp-logo-card--large { min-width: 160px; }
                    .sp-logo-card--medium { min-width: 130px; padding: 16px 24px; }
                    .sp-logo-card--small { min-width: 110px; padding: 12px 20px; }
                    .sp-cta { padding: 28px 20px; flex-direction: column; text-align: center; }
                }
                @media (max-width: 480px) {
                    .sp-logos { gap: 12px; }
                    .sp-logo-card { padding: 14px 20px; }
                }
            `}</style>

            <div className="container">
                {/* Header */}
                <div className="sp-header wow fadeInUp" data-wow-delay="100ms">
                    <div className="sp-header__eyebrow">{section.eyebrow}</div>
                    <h2 className="sp-header__title">
                        {section.title}<br />
                        {section.titleYear} <span>{section.titleAccent}</span>
                    </h2>
                    <p className="sp-header__sub">{section.subtitle}</p>
                </div>

                {/* Tier Rows */}
                {tiers.map((tier, idx) => {
                    if (!tier.sponsors || !tier.sponsors.length) return null;

                    // Determine card size class based on maxCols
                    let sizeClass = 'sp-logo-card--medium';
                    if (tier.maxCols === 1) sizeClass = 'sp-logo-card--title';
                    else if (tier.maxCols === 2) sizeClass = 'sp-logo-card--large';
                    else if (tier.maxCols >= 4) sizeClass = 'sp-logo-card--small';

                    // Build the tier name with accent word highlighted
                    // We split by accentWord safely
                    let tierNameParts = [tier.name, ''];
                    if (tier.accentWord && tier.name.includes(tier.accentWord)) {
                        tierNameParts = tier.name.split(tier.accentWord);
                    }

                    return (
                        <div className="sp-tier wow fadeInUp" data-wow-delay={`${100 + idx * 50}ms`} key={idx}>
                            <div className="sp-tier__heading">
                                <div className="sp-tier__sub">Tier {String(idx + 1).padStart(2, '0')}</div>
                                <h3 className="sp-tier__name">
                                    {tier.accentWord && tier.name.includes(tier.accentWord) ? (
                                        <>
                                            {tierNameParts[0]}<span style={{ color: tier.color || 'var(--eventflow-base)' }}>{tier.accentWord}</span>{tierNameParts[1] || ''}
                                        </>
                                    ) : (
                                        <span style={{ color: tier.color || '#fff' }}>{tier.name}</span>
                                    )}
                                </h3>
                            </div>
                            <div className="sp-logos">
                                {tier.sponsors.map((sponsor, i) => (
                                    <a
                                        className={`sp-logo-card ${sizeClass}`}
                                        href={sponsor.link || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={sponsor.name}
                                        key={i}
                                    >
                                        {sponsor.logo ? (
                                            <img
                                                src={sponsor.logo}
                                                alt={sponsor.name}
                                                style={{ height: tier.logoHeight ? `${tier.logoHeight}px` : 'auto' }}
                                            />
                                        ) : (
                                            <span className="sp-logo-placeholder">{sponsor.name} LOGO HERE</span>
                                        )}
                                    </a>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* CTA */}
                <div className="sp-cta wow fadeInUp" data-wow-delay="200ms">
                    <div className="sp-cta__text">
                        <h4>{section.ctaTitle}</h4>
                        <p>{section.ctaSubtitle}</p>
                    </div>
                    <div className="sp-cta__tiers">
                        {(section.ctaTiers || []).map((t, i) => (
                            <span className="sp-cta__pill" key={i}>{t}</span>
                        ))}
                    </div>
                    <a href={section.ctaButtonLink} className="thm-btn">
                        {section.ctaButtonText} <span className="icon-arrow-right" />
                    </a>
                </div>
            </div>
        </section>
    );
}
